import React from 'react';
import PricingCard from './PricingCard';
import PausedSubscriptionNotice from './PausedSubscriptionNotice';
import {yearlySavings} from '../../domain/plans';
import type {BillingCycleName, PaddleProducts, SubscriptionPlan} from '../../domain/types';
import type {ApiStatus} from '../../store/apiState';

/**
 * Two cards, not three. There is no free tier to put alongside them any more: signup goes through a
 * Paddle trial, so the only choice on offer is monthly or yearly.
 */
interface PricingSectionProps {
    heading: string;
    subheading: string;
    products: PaddleProducts | null;
    status: ApiStatus;
    error: string | null;
    isAuthenticated: boolean;
    currentPlan: SubscriptionPlan | null;
    hasSubscription: boolean;
    hasPausedSubscription: boolean;
    /** The cycle travels with the price id so a signed-out visitor lands on /signup already on it. */
    onSubscribe: (priceId: string, billingCycle: BillingCycleName) => void;
}

const PricingSection: React.FC<PricingSectionProps> = ({
  heading,
  subheading,
  products,
  status,
  error,
  isAuthenticated,
  currentPlan,
  hasSubscription,
  hasPausedSubscription,
  onSubscribe,
}) => {
  if (status === 'pending' || status === 'idle') {
    return (
      <section id="pricing" className="bg-gray-800 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-400">Loading pricing...</p>
        </div>
      </section>
    );
  }

  if (status === 'rejected' || !products) {
    return (
      <section id="pricing" className="bg-gray-800 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-center text-red-400">
            {error || 'Unable to load pricing. Please try refreshing the page.'}
          </p>
        </div>
      </section>
    );
  }

  // A yearly-only bullet, computed from the live prices rather than a hardcoded "Save 20%", is
  // what makes the two cards read as different plans instead of the same list twice at two prices.
  const savings = yearlySavings(products.monthly.price, products.yearly.price);
  const savingsFeature = savings > 0 ? `Save $${savings}/year vs monthly billing` : undefined;

  return (
    <section id="pricing" className="bg-gray-800 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">{heading}</h2>
          <p className="mt-4 text-lg text-gray-400">{subheading}</p>
        </div>

        {hasPausedSubscription && <PausedSubscriptionNotice />}

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <PricingCard
            name={products.monthly.name}
            price={products.monthly.price}
            features={products.monthly.features}
            billingCycle="monthly"
            isAuthenticated={isAuthenticated}
            isCurrentPlan={currentPlan === 'pro_monthly'}
            hasSubscription={hasSubscription}
            currentPlan={currentPlan}
            onSubscribe={() => onSubscribe(products.monthly.priceId, 'monthly')}
          />
          <PricingCard
            name={products.yearly.name}
            price={products.yearly.price}
            features={products.yearly.features}
            savingsFeature={savingsFeature}
            billingCycle="yearly"
            highlight={!hasSubscription}
            isAuthenticated={isAuthenticated}
            isCurrentPlan={currentPlan === 'pro_yearly'}
            hasSubscription={hasSubscription}
            currentPlan={currentPlan}
            onSubscribe={() => onSubscribe(products.yearly.priceId, 'yearly')}
          />
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
